import { CheckCircle2, Circle, Package, Truck } from "lucide-react";
import { money } from "../../lib/api";

const LABELS = {
  pending_payment: ["Awaiting payment", "bg-navy/10 text-navy"],
  paid: ["Paid · preparing", "bg-turq/15 text-[#0078a0]"],
  in_production: ["In production", "bg-sun/40 text-navy"],
  shipped: ["Shipped", "bg-sunset/20 text-[#a85a00]"],
  delivered: ["Delivered", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-red-100 text-red-700"],
  refunded: ["Refunded", "bg-red-100 text-red-700"],
  payment_failed: ["Payment failed", "bg-red-100 text-red-700"],
  expired: ["Checkout expired", "bg-navy/10 text-navy/60"],
};

export const StatusBadge = ({ status }) => {
  const [label, cls] = LABELS[status] || [status, "bg-navy/10 text-navy"];
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${cls}`} data-testid="order-status-badge">{label}</span>;
};

const STEPS = ["paid", "in_production", "shipped", "delivered"];

export const OrderView = ({ o }) => {
  const idx = STEPS.indexOf(o.status);
  const tracking = o.fulfillment?.tracking || [];
  return (
    <div className="space-y-6" data-testid={`order-view-${o.order_number}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-navy/50 font-bold">Order</p>
          <p className="text-2xl font-extrabold text-navy" data-testid="order-number">{o.order_number}</p>
          <p className="text-sm text-navy/60">{new Date(o.created_at).toLocaleDateString()}</p>
        </div>
        <StatusBadge status={o.status} />
      </div>
      {idx >= 0 && (
        <ol className="grid grid-cols-4 gap-2 text-[11px] sm:text-xs font-semibold text-navy" data-testid="order-progress">
          {STEPS.map((s, i) => (
            <li key={s} className="flex flex-col items-center gap-1 text-center">
              {i <= idx ? <CheckCircle2 className="w-6 h-6 text-turq" /> : <Circle className="w-6 h-6 text-navy/20" />}
              {LABELS[s][0].split(" ·")[0]}
            </li>
          ))}
        </ol>
      )}
      <ul className="divide-y divide-border">
        {o.items.map((l) => (
          <li key={`${l.slug}${l.color}${l.size}`} className="py-3 flex gap-3 items-center">
            {l.image && <img src={l.image} alt="" className="w-14 h-14 rounded-xl object-cover" />}
            <div className="flex-1">
              <p className="font-semibold text-navy text-sm">{l.name}</p>
              <p className="text-xs text-navy/60">{l.color} · {l.size} · Qty {l.qty}</p>
            </div>
            <span className="font-semibold text-navy text-sm">{money(l.unit_price * l.qty)}</span>
          </li>
        ))}
      </ul>
      <dl className="text-sm text-navy space-y-1.5">
        <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(o.subtotal)}</dd></div>
        {o.discount_amount > 0 && <div className="flex justify-between text-tpink"><dt>Discount ({o.discount_code})</dt><dd>-{money(o.discount_amount)}</dd></div>}
        <div className="flex justify-between"><dt>Shipping</dt><dd>{o.shipping ? money(o.shipping) : "FREE"}</dd></div>
        {o.tax > 0 && <div className="flex justify-between"><dt>Tax</dt><dd>{money(o.tax)}</dd></div>}
        <div className="flex justify-between font-extrabold text-base pt-2 border-t border-border"><dt>Total</dt><dd data-testid="order-total">{money(o.total)}</dd></div>
      </dl>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        {o.shipping_address?.line1 && (
          <div className="rounded-2xl bg-sand p-4"><p className="font-bold text-navy flex items-center gap-2"><Package className="w-4 h-4" /> Ship to</p>
            <p className="mt-1 text-navy/70">{o.shipping_address.name}<br />{o.shipping_address.line1} {o.shipping_address.line2}<br />{o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}</p></div>
        )}
        <div className="rounded-2xl bg-sand p-4" data-testid="order-tracking"><p className="font-bold text-navy flex items-center gap-2"><Truck className="w-4 h-4" /> Tracking</p>
          {tracking.length ? tracking.map((t) => <a key={t.number} href={t.url} target="_blank" rel="noreferrer" className="block mt-1 text-[#0078a0] underline">{t.carrier} {t.number}</a>)
            : <p className="mt-1 text-navy/70">Tracking appears here once your order ships.</p>}
        </div>
      </div>
    </div>
  );
};
